<script lang="ts">
	import { createSortable, type CreateSortableInput } from "@dnd-kit/svelte/sortable";
	import { Accordion, AccordionItem, Badge } from "flowbite-svelte-v1";
	import { type Snippet } from "svelte";
	import { twMerge } from "tailwind-merge";
	import { FieldType, type Field } from "#gen/forms/v1/field_pb.js";
	import type { Form } from "#gen/forms/v1/forms_pb.js";
	import Icon from "#lib/components/Icon.svelte";
	import P from "#lib/components/P.svelte";
	import ContactProperty from "#lib/components/page/components/form/ContactProperty.svelte";
	import CustomConfig from "#lib/components/page/components/form/CustomConfig.svelte";
	import DefineValidation from "#lib/components/page/components/form/validation/DefineValidation.svelte";
	import { hasValidationRules, isZeroBounceEnabled } from "#lib/components/page/components/form/validation/schemaBuilder.js";
	import FeatureFlagGate from "#lib/permissions/FeatureFlagGate.svelte";
	import Button from "#lib/ui/Button/index.js";

	interface Props extends CreateSortableInput {
		field: Field;
		total?: number;
		active?: boolean;
		editable?: boolean;
		formData?: Form;
		showAdvanced: boolean;
		children?: Snippet;
		onRemove?: (field: string) => void;
		onDuplicate?: (field: string) => void;
		onMoveUp?: (field: string) => void;
		onMoveDown?: (field: string) => void;
		onClick?: (e: MouseEvent) => void;
		onKeydown?: (e: KeyboardEvent) => void;
		onChange?: (field: Field) => void;
		onAdvancedToggle?: (field: Field, open: boolean) => void;
	}

	let { field, id, index, total = 0, active = false, editable = false, formData = undefined, showAdvanced, children, onRemove, onDuplicate, onMoveUp, onMoveDown, onClick, onKeydown, onChange, onAdvancedToggle, ...rest }: Props = $props();

	const { attach, attachHandle, isDragging } = $derived(
		createSortable({
			...rest,
			get id() {
				return id;
			},
			get index() {
				return index;
			},
			get data() {
				return field;
			},
		}),
	);
	const isDecorative = $derived(field.fieldType === FieldType.BLOCK_TEXT || field.fieldType === FieldType.LEGAL);

	$effect(() => {
		onAdvancedToggle?.(field, showAdvanced);
	});

	function remove() {
		onRemove?.(field.name);
	}

	function duplicate() {
		onDuplicate?.(field.name);
	}

	function moveUp(e: Event) {
		e.stopImmediatePropagation();
		onMoveUp?.(field.name);
	}

	function moveDown(e: Event) {
		e.stopImmediatePropagation();
		onMoveDown?.(field.name);
	}
</script>

<div {@attach attach} id="field-editor-{field.name}" onclick={onClick} role="button" onkeydown={onKeydown} tabindex="-1">
	<div class={twMerge("group flex items-center rounded-lg border bg-white px-3 py-6 hover:border-gray-400", active && "border-primary-700 hover:border-primary-700")}>
		<div {@attach attachHandle} aria-label="drag-handle for {field.title}" class={twMerge("mr-2 w-8 flex-none opacity-0 group-hover:opacity-100", (isDragging || active) && "opacity-100")} data-action-name="form-field-reposition" data-action-title={field.name}>
			<Icon iconSet="mingcute" name="dots-line" />
		</div>
		<div class="grow">
			{@render children?.()}
			{#if editable && !isDecorative}
				<FeatureFlagGate flag="setting_form_input_validation">
					<Accordion flush>
						<AccordionItem classes={{ button: "py-4 border-b-0 justify-start", content: "w-full shrink-0 py-0 border-b-0" }} bind:open={showAdvanced} onclick={e => e.stopPropagation()}>
							{#snippet header()}
								<div class="flex w-full cursor-pointer flex-row items-center gap-2">
									<P size="sm" class="mr-2 font-normal text-gray-900">Advanced configuration</P>
									{#if !showAdvanced}
										{#if Boolean(field.modelFieldBinding)}
											<Badge class="bg-green-100 text-green-800">Contact property</Badge>
										{/if}
										{#if hasValidationRules(field.validationSchema)}
											<Badge class="bg-green-100 text-green-800">Validation rule</Badge>
										{/if}
										{#if isZeroBounceEnabled(field.validationSchema)}
											<Badge class="bg-green-100 text-green-800">Email verification</Badge>
										{/if}
									{/if}
								</div>
							{/snippet}
							{#snippet arrowup()}
								<Icon name="chevron-up" class="h-4 w-4 text-black" />
							{/snippet}
							{#snippet arrowdown()}
								<Icon name="chevron-down" class="h-4 w-4 text-black" />
							{/snippet}
							<div class="flex flex-col justify-center gap-2">
								<ContactProperty {field} {formData} {onChange} />
								<DefineValidation {field} {onChange} />
								<CustomConfig {field} {onChange} />
							</div>
						</AccordionItem>
					</Accordion>
					{#snippet fallback()}
						<ContactProperty {field} {formData} {onChange} />
					{/snippet}
				</FeatureFlagGate>
			{/if}
		</div>
		<div class="mx-1 w-8 flex-none opacity-0 group-hover:opacity-100" class:!opacity-100={active}>
			<Button on:click={moveUp} color="light" class="mb-1 border-0 p-2!" disabled={index === 0} data-action-name="form-field-move-up" data-action-title={field.name}>
				<span class="sr-only">Move up</span>
				<Icon name="chevron-up" />
			</Button>
			<Button on:click={duplicate} color="light" class="mb-1 border-0 p-2!" data-action-name="form-field-duplicate" data-action-title={field.name}>
				<span class="sr-only">Duplicate</span>
				<Icon name="document-duplicate" />
			</Button>
			<Button on:click={remove} color="light" class="mb-1 border-0 p-2!" disabled={field.immutable} data-action-name="form-field-delete" data-action-title={field.name}>
				<span class="sr-only">Delete</span>
				<Icon name="trash" />
			</Button>
			<Button on:click={moveDown} color="light" class="border-0 p-2!" disabled={index === total - 1} data-action-name="form-field-move-down" data-action-title={field.name}>
				<span class="sr-only">Move down</span>
				<Icon name="chevron-down" />
			</Button>
		</div>
	</div>
</div>
